import { describeSchoolDetails, listJoin, paymentSchedule } from "@rutamagica/contracts";
import { Download, FileText, PenLine } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useDevelopmentFixtures } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Dialog, Field, SelectField } from "../../components/ui";
import { TripDateField, monthText, type DatePrecision } from "../../components/TripDateField";
import { crmApi } from "../../data/crm";
import { businessApi } from "../../data/crm-business";
import { formatDate } from "../../data/format";
import { paymentMethodLabel } from "../../data/payment-method";
import { commerceApi, type CaseDossier, type CasePayment, type WorkspaceService } from "../../data/workspace";
import { caseFacts, missingCaseData } from "./case-facts";
import { CaseConfirmation } from "./CaseConfirmation";
import { copyLabel, money, serviceNames, stageNames } from "./shared";

const quoteStates: Record<string, string> = {
  draft: "Borrador",
  issued: "Enviada",
  accepted: "Aceptada",
  converted: "Reservada",
  expired: "Vencida",
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="case-sheet__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
const unknown = <span className="case-sheet__unknown">Sin registrar</span>;
const shownCount = (value: number | null, suffix = "") => (value === null ? unknown : `${value}${suffix}`);
/** "" while unknown; a whole number of 0 or more otherwise. */
const countInput = (value: string) => (value.trim() === "" ? null : Math.max(0, Math.round(Number(value))));

/** Customer contact, edited on the customer record (shared by all their sales). */
function ContactEditor({ dossier, onDone, onContactSaved }: { dossier: CaseDossier; onDone: (saved: boolean) => void; onContactSaved?: ((contact: { name: string; email: string; phone: string }) => Promise<void>) | undefined }) {
  const [name, setName] = useState(dossier.customer.name);
  const [organization, setOrganization] = useState(dossier.customer.organization ?? "");
  const school = dossier.service === "school";
  const [phone, setPhone] = useState(dossier.customer.phone ?? "");
  const [email, setEmail] = useState(dossier.customer.email ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!dossier.customer.id) return;
    setBusy(true);
    setError("");
    try {
      if (!useDevelopmentFixtures) {
        // The latest version and language guard against overwriting a newer change.
        const current = await crmApi.customerDetail(dossier.customer.id);
        await crmApi.updateCustomer(dossier.customer.id, {
          expected_version: current.customer.version,
          display_name: name.trim(),
          language: current.customer.language,
          phone_e164: phone.replace(/[\s()-]/g, "") || null,
          email: email.trim() || null,
        });
        // The school lives on the customer profile, so every sale and receipt of theirs prints it.
        if (organization.trim() !== (dossier.customer.organization ?? "")) {
          const profile = await businessApi.profile(dossier.customer.id);
          await businessApi.saveProfile(dossier.customer.id, {
            commandId: crypto.randomUUID(),
            expectedVersion: profile.version,
            data: { ...profile.data, organization: organization.trim(), ...(school && organization.trim() ? { kind: "school" as const } : {}) },
          });
        }
      }
      // The booking keeps the same contact for its confirmations.
      await onContactSaved?.({ name: name.trim(), email: email.trim(), phone: phone.replace(/[\s()-]/g, "") });
      onDone(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No pudimos guardar el cliente.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="case-sheet__form" onSubmit={(event) => void save(event)}>
      <Field label={school ? "Escuela" : "Empresa o institución"} value={organization} maxLength={190} onChange={(e) => setOrganization(e.target.value)} hint={school ? "Sale en los comprobantes, con la persona en pequeño." : "Opcional."} />
      <Field label={school ? "Persona de contacto" : "Nombre"} value={name} maxLength={190} required onChange={(e) => setName(e.target.value)} />
      <Field label="Teléfono" value={phone} placeholder="+5215512345678" onChange={(e) => setPhone(e.target.value)} hint="Con lada del país, por ejemplo +52…" />
      <Field label="Correo" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      {error && <p role="alert" className="case-sheet__error">{error}</p>}
      <div className="case-sheet__form-actions">
        <Button type="submit" disabled={busy || !name.trim()}>{busy ? "Guardando…" : "Guardar"}</Button>
        <Button type="button" variant="quiet" onClick={() => onDone(false)}>Cancelar</Button>
      </div>
    </form>
  );
}

/** Trip and group, edited on the sale: the quote, the payment slip and the receipts read them. */
function GroupEditor({ dossier, onDone }: { dossier: CaseDossier; onDone: (saved: boolean) => void }) {
  const facts = caseFacts(dossier);
  const opportunity = dossier.opportunity!;
  const [service, setService] = useState<WorkspaceService>((dossier.service ?? "school") as WorkspaceService);
  const [date, setDate] = useState(facts.date ?? "");
  const [precision, setPrecision] = useState<DatePrecision>(facts.monthOnly ? "month" : "day");
  const [people, setPeople] = useState(facts.people ? String(facts.people) : "");
  const [teachers, setTeachers] = useState(facts.teachers === null ? "" : String(facts.teachers));
  const [companions, setCompanions] = useState(facts.companions === null ? "" : String(facts.companions));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const school = service === "school";
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!useDevelopmentFixtures)
        await crmApi.updateOpportunityDetails(String(opportunity.id), {
          expected_version: Number(opportunity.version),
          requested_date: date || null,
          ...(date ? { requested_date_precision: precision } : {}),
          people_count: Number(people) > 0 ? Math.round(Number(people)) : null,
          ...(school ? { teachers_count: countInput(teachers), companions_count: countInput(companions) } : {}),
          ...(service !== dossier.service ? { service_type: service } : {}),
        });
      onDone(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No pudimos guardar los datos.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="case-sheet__form" onSubmit={(event) => void save(event)}>
      <SelectField label="Servicio" value={service} onChange={(e) => setService(e.target.value as WorkspaceService)}>
        <option value="school">Grupos escolares</option>
        <option value="balloon">Vuelos en globo</option>
        <option value="tour">Tours</option>
      </SelectField>
      <TripDateField label="Fecha" name="sheet_date" value={date} precision={precision} onChange={(next, nextPrecision) => { setDate(next); setPrecision(nextPrecision); }} />
      <div className="case-sheet__group">
        <Field label={school ? "Alumnos" : "Personas"} type="number" min="1" max="5000" value={people} onChange={(e) => setPeople(e.target.value)} />
        {school && <Field label="Profesores" type="number" min="0" max="1000" value={teachers} onChange={(e) => setTeachers(e.target.value)} hint="Cortesía" />}
        {school && <Field label="Acompañantes" type="number" min="0" max="1000" value={companions} onChange={(e) => setCompanions(e.target.value)} hint="0 si no va nadie" />}
      </div>
      {error && <p role="alert" className="case-sheet__error">{error}</p>}
      <div className="case-sheet__form-actions">
        <Button type="submit" disabled={busy}>{busy ? "Guardando…" : "Guardar"}</Button>
        <Button type="button" variant="quiet" onClick={() => onDone(false)}>Cancelar</Button>
      </div>
    </form>
  );
}

/**
 * The case file in one sheet: customer, trip and group, sale, money, quotes, payments and the
 * documents for the customer; the case adds its booking, participants and documents below.
 * Customer and group are edited here, on their one record.
 */
export function CaseSheet({ dossier, open, onClose, onSaved, onOpenQuotes, conversationId, canSend, startEditing = false, focus = "", bookingChanges, onContactSaved, children }: {
  dossier: CaseDossier;
  open: boolean;
  onClose: () => void;
  /** Reloads the case after an edit, so every part of the screen shows the new data. */
  onSaved?: () => void;
  conversationId: string | null;
  canSend: boolean;
  /** Opened to fill missing data (e.g. before recording a payment): the group editor is open. */
  startEditing?: boolean;
  /** Closes the sheet and opens the quotes beside the chat. */
  onOpenQuotes?: (() => void) | undefined;
  /** The part to show first (a block's `data-section`), e.g. "Documentos" from an alert. */
  focus?: string;
  /** With a booking: "Cambiar fecha" and "Cambiar grupo", beside the date and group they change. */
  bookingChanges?: ReactNode;
  /** With a booking: the customer's new contact is saved on the booking too. */
  onContactSaved?: ((contact: { name: string; email: string; phone: string }) => Promise<void>) | undefined;
  children?: ReactNode;
}) {
  const { hasPermission } = useAuth();
  const [payments, setPayments] = useState<CasePayment[]>([]);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState<"" | "contact" | "group">("");
  const sheetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) setEditing(startEditing ? "group" : "");
  }, [open, startEditing]);
  useEffect(() => {
    if (!open || !focus) return;
    const frame = requestAnimationFrame(() => {
      const block = sheetRef.current?.querySelector<HTMLElement>(`[data-section="${CSS.escape(focus)}"]`);
      block?.scrollIntoView?.({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, focus]);
  useEffect(() => {
    if (!open || !dossier.permissions.finance) return;
    let active = true;
    commerceApi.casePayments(dossier.key).then((result) => { if (active) setPayments(result.data); }, () => undefined);
    return () => { active = false; };
  }, [open, dossier.key, dossier.permissions.finance]);

  const booking = dossier.bookings[0];
  const opportunity = dossier.opportunity;
  const facts = caseFacts(dossier);
  const { quote, school, date } = facts;
  const missing = missingCaseData(dossier);
  const canEditGroup = Boolean(opportunity && !booking && hasPermission("crm.write"));
  const canEditContact = Boolean(dossier.customer.id && hasPermission("crm.write"));
  const summary = (booking?.payment_summary ?? null) as Record<string, unknown> | null;
  const manual = (Array.isArray(summary?.manual_payments) ? summary!.manual_payments : []) as { amount?: number; method?: string; paid_on?: string }[];
  // Card or checkout payments reach the case as WooCommerce records, not as case payments.
  const records = payments.length || manual.length ? [] : (dossier.payments ?? []).filter((row) => Number(row.amount ?? 0) > 0);
  const paid = summary ? Number(summary.paid_amount ?? 0) : payments.reduce((sum, row) => sum + row.amount, 0) + records.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
  const total = summary ? Number(summary.contract_total ?? quote?.total ?? 0) : quote?.total ?? 0;
  const schedule = quote ? paymentSchedule(quote.payment_terms ?? null, total, date ?? undefined, paid) : null;
  const rest = Math.max(0, Math.round((total - paid) * 100) / 100);
  const listed = payments.reduce((sum, row) => sum + row.amount, 0) + manual.reduce((sum, row) => sum + Number(row.amount ?? 0), 0)
    + records.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
  const unlisted = Math.round((paid - listed) * 100) / 100;
  const orderId = booking?.order_source_id ?? dossier.orders?.[0]?.order_number ?? dossier.orders?.[0]?.sourceId;
  const levels = school ? describeSchoolDetails(quote?.school) : [];
  const nextFollowup = (dossier.followups ?? []).find((item) => item.status === "pending");
  const done = (saved: boolean) => {
    setEditing("");
    if (saved) onSaved?.();
  };
  const run = async (key: string, task: () => Promise<unknown>) => {
    setBusy(key);
    try {
      if (!useDevelopmentFixtures) await task();
    } finally {
      setBusy("");
    }
  };
  const editButton = (target: "contact" | "group", label: string) => (
    <button type="button" className="case-sheet__edit" onClick={() => setEditing(target)} aria-label={label}>
      <PenLine size={14} aria-hidden="true" /> Editar
    </button>
  );

  return (
    <Dialog
      open={open}
      size="executive"
      className="case-sheet-dialog"
      title={dossier.title}
      eyebrow="Expediente"
      onClose={onClose}
      footer={
        <div className="case-sheet__footer">
          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
        </div>
      }
    >
      {missing.length > 0 && (
        <p className="case-sheet__missing" role="status">
          Faltan datos: {listJoin(missing)}.{canEditGroup && editing !== "group" ? " " : ""}
          {canEditGroup && editing !== "group" && <button type="button" className="case-sheet__edit" onClick={() => setEditing("group")}>Llenar ahora</button>}
        </p>
      )}
      <div className="case-sheet" ref={sheetRef}>
        <section className="case-sheet__block" aria-label="Cliente">
          <header><h3>Cliente</h3>{canEditContact && editing !== "contact" && editButton("contact", "Editar cliente")}</header>
          {editing === "contact" ? <ContactEditor dossier={dossier} onDone={done} onContactSaved={onContactSaved} /> : (
            <dl>
              {(school || dossier.customer.organization) && <Row label={school ? "Escuela" : "Empresa"}>{dossier.customer.organization || unknown}</Row>}
              <Row label={school ? "Contacto" : "Nombre"}>{dossier.customer.name}</Row>
              <Row label="Servicio">{dossier.service ? serviceNames[dossier.service] : "Por definir"}</Row>
              <Row label="Teléfono">{dossier.customer.phone ? <a href={`tel:${dossier.customer.phone}`}>{dossier.customer.phone}</a> : unknown}</Row>
              <Row label="Correo">{dossier.customer.email ? <a href={`mailto:${dossier.customer.email}`}>{dossier.customer.email}</a> : unknown}</Row>
            </dl>
          )}
        </section>

        <section className="case-sheet__block" aria-label="Viaje y grupo">
          <header><h3>{school ? "Visita y grupo" : "Viaje y grupo"}</h3>{canEditGroup && editing !== "group" && editButton("group", "Editar visita y grupo")}</header>
          {editing === "group" && opportunity ? <GroupEditor dossier={dossier} onDone={done} /> : (
            <dl>
              <Row label="Fecha">{date ? (facts.monthOnly ? `${monthText(date)} · día por confirmar` : formatDate(date)) : unknown}</Row>
              {facts.time && <Row label="Horario">{facts.time}</Row>}
              <Row label={school ? "Alumnos" : "Personas"}>{shownCount(facts.people)}</Row>
              {school && <Row label="Profesores">{shownCount(facts.teachers, facts.teachers ? " · cortesía" : "")}</Row>}
              {school && <Row label="Acompañantes">{shownCount(facts.companions)}</Row>}
              {levels.map(({ level, grades, workshops }) => (
                <Row key={level.id} label={level.name}>
                  {[grades.length ? `${listJoin(grades)} ${level.gradeUnit}${grades.length > 1 ? "s" : ""}` : "", workshops.length ? `Talleres: ${listJoin(workshops.map((w) => w.name))}` : ""].filter(Boolean).join(" · ") || "Sin detalle"}
                </Row>
              ))}
              {school && quote?.school?.menu && <Row label="Menú">{quote.school.menu}</Row>}
            </dl>
          )}
          {booking && editing !== "group" && bookingChanges}
        </section>


        {dossier.permissions.finance && (
          <section className="case-sheet__block" aria-label="Dinero">
            <header><h3>Dinero</h3></header>
            <dl>
              <Row label="Total">{total > 0 ? money(total, quote?.currency ?? "MXN") : "Sin cotización"}</Row>
              <Row label="Pagado">{money(paid, quote?.currency ?? "MXN")}</Row>
              {total > 0 && (
                <Row label="Resta">
                  {money(rest, quote?.currency ?? "MXN")}
                  {rest > 0 && schedule?.dueDate ? ` · ${schedule.dayBefore ? "a más tardar el" : "el"} ${formatDate(schedule.dueDate)}` : rest > 0 ? "" : " · pagado por completo"}
                </Row>
              )}
            </dl>
          </section>
        )}

        {Boolean(opportunity?.stage || booking || nextFollowup) && <section className="case-sheet__block" aria-label="Venta">
          <header><h3>Estado</h3></header>
          <dl>
            {Boolean(opportunity?.stage) && <Row label="Etapa">{stageNames[String(opportunity!.stage)] ?? String(opportunity!.stage)}</Row>}
            {booking && <Row label="Reserva">RES-{String(booking.source_id ?? booking.id)} · {stageNames[String(booking.status)] ?? String(booking.status ?? "")}</Row>}
            {nextFollowup && <Row label="Próxima acción">{String(nextFollowup.note ?? "Seguimiento")} · {formatDate(String(nextFollowup.due_at ?? "").slice(0, 10))}</Row>}
          </dl>
        </section>}


        {dossier.permissions.finance && (
          <section className="case-sheet__block case-sheet__block--wide" aria-label="Pagos">
            <header><h3>Pagos</h3></header>
            {payments.length || manual.length || records.length || unlisted > 0 ? (
              <ul className="case-sheet__list">
                {payments.map((row) => (
                  <li key={`order-${row.order_id}`}>
                    <span>
                      <strong>{money(row.amount, row.currency || "MXN")}</strong>
                      <small>{formatDate(row.paid_on)} · {paymentMethodLabel(row.method)} · pedido #{row.order_id}</small>
                    </span>
                    <Button variant="secondary" icon={Download} disabled={!!busy} onClick={() => void run(`receipt:${row.order_id}`, () => commerceApi.downloadCaseReceipt(row.order_id, dossier.key, dossier.service))}>Comprobante</Button>
                  </li>
                ))}
                {!payments.length && manual.map((row, index) => (
                  <li key={`manual-${index}`}>
                    <span>
                      <strong>{money(Number(row.amount ?? 0), quote?.currency ?? "MXN")}</strong>
                      <small>{row.paid_on ? formatDate(row.paid_on) : ""} · {paymentMethodLabel(row.method)}</small>
                    </span>
                  </li>
                ))}
                {records.map((row) => (
                  <li key={`record-${row.id}`}>
                    <span>
                      <strong>{money(Number(row.amount ?? 0), String(row.currency ?? "MXN"))}</strong>
                      <small>{row.paid_at ? formatDate(String(row.paid_at).slice(0, 10)) : ""} · {String(row.provider ?? "en línea")} · {copyLabel(row.status)}</small>
                    </span>
                  </li>
                ))}
                {unlisted > 0 && (
                  <li key="online">
                    <span>
                      <strong>{money(unlisted, quote?.currency ?? "MXN")}</strong>
                      <small>Pagado en línea al reservar{orderId ? ` · pedido #${String(orderId)}` : ""}</small>
                    </span>
                  </li>
                )}
              </ul>
            ) : <p className="workspace-empty">Sin pagos registrados.</p>}
            <CaseConfirmation dossier={dossier} paid={paid} conversationId={conversationId} canSend={canSend} />
          </section>
        )}
        <section className="case-sheet__block case-sheet__block--wide" aria-label="Cotizaciones" data-section="Cotizaciones">
          <header>
            <h3>Cotizaciones</h3>
            {onOpenQuotes && (
              <button type="button" className="case-sheet__edit" onClick={onOpenQuotes}>
                <FileText size={14} aria-hidden="true" /> Cotizar
              </button>
            )}
          </header>
          {dossier.quotes.length ? (
            <ul className="case-sheet__list">
              {[...dossier.quotes].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).map((item) => (
                <li key={item.id}>
                  <span>
                    <strong>Folio {item.id.slice(0, 8).toUpperCase()} · v{item.version}</strong>
                    <small>{quoteStates[item.status] ?? item.status} · {formatDate(item.updated_at.slice(0, 10))}{dossier.permissions.finance ? ` · ${money(item.total, item.currency)}` : ""}</small>
                  </span>
                  {item.status !== "draft" && (
                    <Button variant="secondary" icon={Download} disabled={!!busy} onClick={() => void run(`quote:${item.id}`, () => commerceApi.downloadPdf(item.id))}>PDF</Button>
                  )}
                </li>
              ))}
            </ul>
          ) : <p className="workspace-empty">Aún sin cotización.</p>}
        </section>
        {children}
      </div>
    </Dialog>
  );
}
